import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Headset, Loader2, LogOut, RotateCcw, Search, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SupportThread from '@/components/support/SupportThread';
import { adminAPI, adminSession } from '@/lib/adminApi';

const QUEUE_POLL_MS = 10000;
const SMS_GUARD_POLL_MS = 60000;
const STATUSES = [
  { key: 'open', label: 'Open' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
];

function displayName(user) {
  if (!user) return '';
  return user.business_name || user.full_name || user.phone || 'User';
}

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

/**
 * Red bar on every portal page while the SMS limiter has texting paused
 * (more than the limit went out in a short time; utils/sms_guard.py).
 * Texting stays off until someone here turns it back on.
 */
function SmsPausedBanner() {
  const [guard, setGuard] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminAPI.smsGuard().then((res) => setGuard(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, SMS_GUARD_POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  if (!guard?.paused) return null;

  const resume = async () => {
    if (!window.confirm('Turn text messages back on? Check first that the texts were not an attack (many sign-ups from unknown numbers).')) return;
    setBusy(true);
    try {
      setGuard((await adminAPI.resumeSms()).data);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-red-600 text-white" role="alert" data-testid="sms-paused-banner">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
        <AlertTriangle className="w-5 h-5 shrink-0" />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-semibold">Text messages are paused</p>
          <p className="text-white/90">
            {guard.paused_reason} at {new Date(guard.paused_at).toLocaleString()}. No sign-up codes,
            password-reset codes or shop invites are being sent until someone turns texting back on.
          </p>
        </div>
        <Button size="sm" className="bg-white text-red-700 hover:bg-white/90" disabled={busy} onClick={resume}
                data-testid="sms-resume">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Turn texting back on'}
        </Button>
      </div>
    </div>
  );
}

/** Signed-in frame for every portal page: header, tabs, sign out. */
export function AdminShell() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(adminSession.admin());

  useEffect(() => {
    if (!adminSession.token()) return;
    adminAPI.me().then((res) => setAdmin(res.data)).catch(() => {});
  }, []);

  if (!adminSession.token()) return <Navigate to="/admin" replace />;

  const signOut = () => {
    adminSession.clear();
    navigate('/admin', { replace: true });
  };
  const tab = ({ isActive }) =>
    `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
      isActive ? 'bg-white text-farm-forest' : 'text-white/90 hover:bg-white/10'}`;

  return (
    <div className="min-h-screen bg-farm-cream flex flex-col">
      <header className="bg-farm-primary text-white">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-2 font-heading font-bold">
            <Headset className="w-5 h-5" /> African Farm Support
          </span>
          <nav className="flex items-center gap-1 ml-2">
            <NavLink to="/admin/chats" className={tab} data-testid="admin-tab-chats">Chats</NavLink>
            <NavLink to="/admin/products" className={tab} data-testid="admin-tab-products">NAFDAC list</NavLink>
            <NavLink to="/admin/clusters" className={tab} data-testid="admin-tab-clusters">Clusters</NavLink>
            {admin?.role === 'superuser' && (
              <NavLink to="/admin/team" className={tab} data-testid="admin-tab-team">
                <Users className="w-4 h-4" /> Team
              </NavLink>
            )}
          </nav>
          <span className="ml-auto text-sm text-white/85 truncate">{admin?.name || admin?.email}</span>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={signOut}
                  data-testid="admin-sign-out">
            <LogOut className="w-4 h-4 mr-1" /> Sign out
          </Button>
        </div>
      </header>
      <SmsPausedBanner />
      <main className="flex-1 max-w-6xl w-full mx-auto">
        <Outlet context={{ admin }} />
      </main>
    </div>
  );
}

/** The queue of support chats, with search, and the open chat beside it. */
export function AdminChatsPage() {
  const navigate = useNavigate();
  const { threadId } = useParams();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('open');
  const [data, setData] = useState({ threads: [], total: 0, unread: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(timer);
  }, [q]);

  const loadQueue = useCallback(async () => {
    try {
      const res = await adminAPI.queue({ q: query || undefined, status, limit: 50 });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [query, status]);

  useEffect(() => {
    setLoading(true);
    void loadQueue();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void loadQueue();
    }, QUEUE_POLL_MS);
    return () => clearInterval(timer);
  }, [loadQueue]);

  return (
    <div className="md:grid md:grid-cols-[360px_1fr] md:h-[calc(100vh-56px)]">
      <section className={`bg-white md:border-r border-gray-200 md:overflow-y-auto ${threadId ? 'hidden md:block' : ''}`}>
        <div className="p-3 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-farm-forest/50" />
            <Input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, business, phone or message"
              className="pl-9"
              data-testid="admin-search"
            />
          </div>
          <div className="flex items-center gap-1 mt-2">
            {STATUSES.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setStatus(s.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  status === s.key ? 'bg-farm-primary text-white' : 'bg-gray-100 text-farm-forest'}`}
                data-testid={`admin-status-${s.key}`}
              >
                {s.label}
              </button>
            ))}
            <span className="ml-auto text-xs text-farm-forest/60">
              {data.unread > 0 ? `${data.unread} unread` : `${data.total} chats`}
            </span>
          </div>
        </div>
        {loading && data.threads.length === 0 ? (
          <div className="p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-farm-primary" /></div>
        ) : data.threads.length === 0 ? (
          <p className="p-6 text-sm text-center text-farm-forest/60" data-testid="admin-queue-empty">
            {query ? 'No chats match your search.' : 'No chats here yet.'}
          </p>
        ) : (
          <ul data-testid="admin-queue">
            {data.threads.map((th) => (
              <li key={th.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/chats/${th.id}`)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-farm-cream/60 ${
                    th.id === threadId ? 'bg-farm-cream' : ''}`}
                  data-testid="admin-queue-item"
                >
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold text-sm truncate ${th.unread ? 'text-farm-forest' : 'text-farm-forest/80'}`}>
                      {displayName(th.user)}
                    </span>
                    <span className="text-[11px] text-farm-forest/50 capitalize">{th.user.user_type}</span>
                    <span className="ml-auto text-[11px] text-farm-forest/50">{timeAgo(th.last_message_at)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-xs truncate ${th.unread ? 'text-farm-forest font-medium' : 'text-farm-forest/60'}`}>
                      {th.last_sender === 'admin' ? 'You: ' : ''}{th.last_preview}
                    </span>
                    {th.unread > 0 && (
                      <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold grid place-items-center">
                        {th.unread}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className={`${threadId ? 'flex' : 'hidden md:flex'} md:overflow-y-auto min-h-[calc(100vh-56px)] md:min-h-0`}>
        {threadId ? (
          <AdminThread key={threadId} threadId={threadId} onChanged={loadQueue} />
        ) : (
          <div className="m-auto text-center text-farm-forest/60 p-10">
            <Headset className="w-10 h-10 mx-auto mb-2 text-farm-forest/30" />
            Choose a chat from the list.
          </div>
        )}
      </section>
    </div>
  );
}

function AdminThread({ threadId, onChanged }) {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);

  const load = useCallback(async (after) => {
    const res = await adminAPI.thread(threadId, after);
    setMeta({ user: res.data.user, thread: res.data.thread });
    return res.data;
  }, [threadId]);
  const send = useCallback(async ({ body, image }) => (await adminAPI.reply(threadId, { body, image })).data, [threadId]);
  const markRead = useCallback(async () => {
    try {
      await adminAPI.read(threadId);
      onChanged?.();
    } catch {
      // Fine: the queue refreshes on its own.
    }
  }, [threadId, onChanged]);

  const toggleStatus = async () => {
    const next = meta?.thread?.status === 'closed' ? 'open' : 'closed';
    const res = await adminAPI.setStatus(threadId, next);
    setMeta((m) => ({ ...m, thread: res.data }));
    onChanged?.();
  };

  const user = meta?.user;
  const closed = meta?.thread?.status === 'closed';
  return (
    <div className="flex flex-col w-full min-h-full" data-testid="admin-thread">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 py-2 flex items-center gap-3">
        <Button variant="ghost" size="sm" className="md:hidden" onClick={() => navigate('/admin/chats')}>Back</Button>
        <div className="min-w-0">
          <p className="font-semibold truncate" data-testid="admin-thread-user">{displayName(user)}</p>
          {user && (
            <p className="text-xs text-farm-forest/60 truncate">
              <span className="capitalize">{user.user_type}</span>
              {user.full_name && user.business_name ? ` · ${user.full_name}` : ''}
              {user.phone ? ` · ${user.phone}` : ''}
              {user.location ? ` · ${user.location}` : ''}
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" className="ml-auto" onClick={toggleStatus} data-testid="admin-thread-status">
          {closed
            ? <><RotateCcw className="w-4 h-4 mr-1" /> Reopen</>
            : <><CheckCircle2 className="w-4 h-4 mr-1" /> Mark resolved</>}
        </Button>
      </div>
      <SupportThread
        threadKey={threadId}
        load={load}
        send={send}
        markRead={markRead}
        senderLabel={(m) => (m.sender === 'admin' ? (m.admin_name || 'Support') : null)}
        messagesClassName="flex-1"
        composerClassName="sticky bottom-0 z-20"
      />
    </div>
  );
}
